import { useNavigate } from 'react-router-dom';
import { useBuilderTemplates } from '../hooks/useBuilderTemplates';
import { useBuilderSessions } from '../hooks/useBuilderSessions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, Star, FileCode } from 'lucide-react';

export function TemplatesPage() {
  const navigate = useNavigate();
  const { templates, isLoading, error } = useBuilderTemplates();
  const { createSession, isCreating } = useBuilderSessions();

  const handleSelectTemplate = async (templateId: string) => {
    await createSession({ templateId });
  };

  const handleStartBlank = async () => {
    await createSession();
  };

  // Group templates by category
  const templatesByCategory = templates.reduce(
    (acc, template) => {
      const category = template.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(template);
      return acc;
    },
    {} as Record<string, typeof templates>,
  );

  // Get featured templates
  const featuredTemplates = templates.filter((t) => t.isFeatured);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/a/arche_builder')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Choose a Template</h1>
          <p className="text-muted-foreground mt-1">
            Start with a pre-built template or create from scratch
          </p>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error.message}</p>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {!isLoading && (
        <>
          {/* Blank canvas option */}
          <Card
            className="mb-8 cursor-pointer hover:shadow-md transition-shadow border-dashed"
            onClick={handleStartBlank}
          >
            <CardContent className="flex items-center gap-4 py-6">
              <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                <FileCode className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Start from Scratch</h3>
                <p className="text-sm text-muted-foreground">
                  Begin with a blank canvas and build your Arche from the ground up
                </p>
              </div>
              <Button disabled={isCreating}>
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Get Started'}
              </Button>
            </CardContent>
          </Card>

          {/* Featured templates */}
          {featuredTemplates.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                Featured Templates
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {featuredTemplates.map((template) => (
                  <TemplateCard
                    key={template.templateId}
                    template={template}
                    onSelect={() => handleSelectTemplate(template.templateId)}
                    isCreating={isCreating}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Templates by category */}
          {Object.entries(templatesByCategory).map(([category, categoryTemplates]) => (
            <div key={category} className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{category}</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {categoryTemplates.map((template) => (
                  <TemplateCard
                    key={template.templateId}
                    template={template}
                    onSelect={() => handleSelectTemplate(template.templateId)}
                    isCreating={isCreating}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Empty state */}
          {templates.length === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <p className="text-muted-foreground">
                  No templates available yet. Start from scratch!
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

interface TemplateCardProps {
  template: {
    templateId: string;
    name: string;
    description: string | null;
    category: string | null;
    thumbnailRef: string | null;
    isFeatured: boolean;
  };
  onSelect: () => void;
  isCreating: boolean;
}

function TemplateCard({ template, onSelect, isCreating }: TemplateCardProps) {
  return (
    <Card
      className="group cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
      onClick={onSelect}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-muted relative">
        {template.thumbnailRef ? (
          <img
            src={template.thumbnailRef}
            alt={template.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileCode className="h-12 w-12 text-muted-foreground/50" />
          </div>
        )}
        {template.isFeatured && (
          <Badge className="absolute top-2 right-2 bg-yellow-500">
            <Star className="h-3 w-3 mr-1" />
            Featured
          </Badge>
        )}
      </div>

      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{template.name}</CardTitle>
        {template.category && (
          <Badge variant="outline" className="w-fit text-xs">
            {template.category}
          </Badge>
        )}
      </CardHeader>

      <CardContent>
        <CardDescription className="line-clamp-2">
          {template.description || 'No description available'}
        </CardDescription>

        <Button
          className="w-full mt-4 opacity-0 group-hover:opacity-100 transition-opacity"
          disabled={isCreating}
        >
          {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Use Template'}
        </Button>
      </CardContent>
    </Card>
  );
}
